// components/DeliveryAgentDashboard.js
import React, { useState, useEffect } from 'react';
import api from '../api';

const DeliveryAgentDashboard = () => {
  const [agentData, setAgentData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('current'); // 'current', 'available', 'history'
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);
  const [autoAssignMessage, setAutoAssignMessage] = useState('');

    // Add the getStatusStyle function here
const getStatusStyle = (status) => {
  const statusColors = {
    // Order Statuses
    'pending': { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' },
    'confirmed': { bg: '#d1ecf1', text: '#0c5460', border: '#bee5eb' },
    'assigned': { bg: '#cce7ff', text: '#004085', border: '#b3d7ff' },
    'picked_up': { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' },
    'in_transit': { bg: '#ffeaa7', text: '#856404', border: '#fdcb6e' },
    'delivered': { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
    'cancelled': { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
    'processing': { bg: '#e2e3e5', text: '#383d41', border: '#d6d8db' },
    'shipped': { bg: '#cce7ff', text: '#004085', border: '#b3d7ff' },
  };
  
  return statusColors[status] || { bg: '#e9ecef', text: '#495057', border: '#dee2e6' };
};

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
  console.log('🔍 DEBUG - Current Assignment Data:', currentAssignment);
  console.log('🔍 DEBUG - Available Orders:', availableOrders);
}, [currentAssignment, availableOrders]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard data, assigned orders, and available orders
      const [dashboardResponse, availableOrdersResponse] = await Promise.all([
        api.get('/api/dashboard/'),
        api.get('/api/available-orders/') // New API call for available orders
      ]);

      setAgentData(dashboardResponse.data.agent);
      setAssignments(dashboardResponse.data.assignments || []);
      setCurrentAssignment(dashboardResponse.data.current_assignment);
      setAvailableOrders(availableOrdersResponse.data.available_orders || []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Auto-assign orders function
  const autoAssignOrders = async () => {
    try {
      setAutoAssignLoading(true);
      setAutoAssignMessage('');
      
      const response = await api.post('/api/delivery/auto-assign/', {
        delivery_agent_id: agentData?.id
      });

      if (response.data.success) {
        setAutoAssignMessage(`✅ ${response.data.message}`);
        // Refresh dashboard data to show newly assigned orders
        fetchDashboardData();
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setAutoAssignMessage('');
        }, 5000);
      } else {
        setAutoAssignMessage(`❌ ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error in auto-assignment:', error);
      setAutoAssignMessage('❌ Error assigning orders. Please try again.');
    } finally {
      setAutoAssignLoading(false);
    }
  };

  // NEW: Update agent availability status
  const updateAgentAvailability = async (isAvailable) => {
    try {
      await api.patch('/api/delivery/agent/status/', {
        is_available: isAvailable
      });
      
      // Update local state
      setAgentData(prev => ({
        ...prev,
        is_available: isAvailable
      }));
    } catch (error) {
      console.error('Error updating agent availability:', error);
      alert('Failed to update availability status');
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      const response = await api.post('/api/accept-order/', {
        order_id: orderId
      });

      if (response.data.success) {
        alert('Order accepted successfully!');
        fetchDashboardData(); // Refresh all data
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error accepting order:', error);
      alert('Failed to accept order. Please try again.');
    }
  };

  const updateDeliveryStatus = async (assignmentId, newStatus) => {
    try {
      await api.post('/api/update-order-status/', {
        assignment_id: assignmentId,
        status: newStatus
      });
      fetchDashboardData();
      alert(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update delivery status');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #e0e0e0' }}>
        <div>
          <h1 style={{ color: '#2e7d32', margin: 0, fontSize: '28px' }}>RurDel Delivery</h1>
          <p style={{ color: '#666', margin: '5px 0 0 0' }}>Delivery Partner Dashboard</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Welcome, {agentData.user.first_name}!</p>
          <button 
            onClick={() => {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              window.location.href = '/';
            }}
            style={{
              backgroundColor: '#d32f2f',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Welcome Message */}
      <div style={{ 
        backgroundColor: '#e8f5e8', 
        padding: '25px', 
        borderRadius: '10px', 
        marginBottom: '25px',
        borderLeft: '5px solid #2e7d32'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ color: '#2e7d32', margin: '0 0 10px 0', fontSize: '24px' }}>
              Welcome , {agentData.user.first_name}! 👋
            </h2>
            <p style={{ color: '#555', margin: '0', fontSize: '16px', lineHeight: '1.5' }}>
              Thank you for being an important part of the RurDel team! 
              {currentAssignment 
                ? ` You have an active delivery assignment ready for you below.` 
                : ` You're all caught up! New delivery assignments will appear here when available.`
              }
            </p>
            <p style={{ color: '#2e7d32', margin: '10px 0 0 0', fontWeight: 'bold', fontSize: '14px' }}>
              Your Vehicle: {agentData.vehicle_type} ({agentData.vehicle_number}) • Status: {agentData.is_available ? 'Available' : 'On Delivery'}
            </p>
          </div>
          
          {/* Auto-Assign Button Section */}
          <div style={{ marginLeft: '20px', minWidth: '200px' }}>
            <button
              onClick={autoAssignOrders}
              disabled={autoAssignLoading || !agentData.is_available || availableOrders.length === 0}
              style={{
                backgroundColor: 
                  autoAssignLoading || !agentData.is_available || availableOrders.length === 0 
                    ? '#cccccc' 
                    : '#2e7d32',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                cursor: 
                  autoAssignLoading || !agentData.is_available || availableOrders.length === 0 
                    ? 'not-allowed' 
                    : 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                width: '100%',
                marginBottom: '10px'
              }}
            >
              {autoAssignLoading ? '🔄 Assigning...' : '🚀 Auto-Assign Orders'}
            </button>
            
            {/* Availability Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>Available:</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                <input
                  type="checkbox"
                  checked={agentData.is_available}
                  onChange={(e) => updateAgentAvailability(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: agentData.is_available ? '#4caf50' : '#ccc',
                  transition: '0.4s',
                  borderRadius: '24px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '18px',
                    width: '18px',
                    left: agentData.is_available ? '28px' : '4px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.4s',
                    borderRadius: '50%'
                  }} />
                </span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Auto-assign Message */}
        {autoAssignMessage && (
          <div style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: autoAssignMessage.includes('✅') ? '#d4edda' : '#f8d7da',
            border: `1px solid ${autoAssignMessage.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '4px',
            color: autoAssignMessage.includes('✅') ? '#155724' : '#721c24'
          }}>
            {autoAssignMessage}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
        <div style={{ backgroundColor: '#e8f5e8', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>Total Deliveries</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, color: '#2e7d32' }}>{assignments.length}</p>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>All-time completed</p>
        </div>
        
        <div style={{ backgroundColor: '#e3f2fd', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#1565c0' }}>Current Status</h3>
          <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#1565c0' }}>
            {agentData.is_available ? 'Available' : 'On Delivery'}
          </p>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            {agentData.is_available ? 'Ready for new orders' : 'Currently delivering'}
          </p>
        </div>
        
        <div style={{ backgroundColor: '#fff3e0', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#e65100' }}>Active Now</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, color: '#e65100' }}>
            {currentAssignment ? '1' : '0'}
          </p>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            {currentAssignment ? 'Active delivery' : 'No active deliveries'}
          </p>
        </div>

        <div style={{ backgroundColor: '#f3e5f5', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#7b1fa2' }}>Available Orders</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, color: '#7b1fa2' }}>
            {availableOrders.length}
          </p>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            Ready for assignment
          </p>
        </div>
      </div>

{/* Tabs Navigation */}
<div style={{ marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
  <div style={{ display: 'flex', gap: '0' }}>
    <button
      onClick={() => setActiveTab('current')}
      style={{
        padding: '12px 24px',
        border: 'none',
        backgroundColor: activeTab === 'current' ? '#2e7d32' : '#f5f5f5',
        color: activeTab === 'current' ? 'white' : '#333',
        cursor: 'pointer',
        borderBottom: activeTab === 'current' ? '2px solid #2e7d32' : '2px solid transparent',
        fontWeight: activeTab === 'current' ? 'bold' : 'normal',
        borderRadius: '8px 8px 0 0',
        transition: 'all 0.3s ease',
        marginRight: '5px'
      }}
    >
      📦 Current Delivery {currentAssignment && '(1)'}
    </button>
    <button
      onClick={() => setActiveTab('available')}
      style={{
        padding: '12px 24px',
        border: 'none',
        backgroundColor: activeTab === 'available' ? '#2196f3' : '#f5f5f5',
        color: activeTab === 'available' ? 'white' : '#333',
        cursor: 'pointer',
        borderBottom: activeTab === 'available' ? '2px solid #2196f3' : '2px solid transparent',
        fontWeight: activeTab === 'available' ? 'bold' : 'normal',
        borderRadius: '8px 8px 0 0',
        transition: 'all 0.3s ease',
        marginRight: '5px'
      }}
    >
      📋 Available Orders ({availableOrders.length})
    </button>
    <button
      onClick={() => setActiveTab('history')}
      style={{
        padding: '12px 24px',
        border: 'none',
        backgroundColor: activeTab === 'history' ? '#ff9800' : '#f5f5f5',
        color: activeTab === 'history' ? 'white' : '#333',
        cursor: 'pointer',
        borderBottom: activeTab === 'history' ? '2px solid #ff9800' : '2px solid transparent',
        fontWeight: activeTab === 'history' ? 'bold' : 'normal',
        borderRadius: '8px 8px 0 0',
        transition: 'all 0.3s ease'
      }}
    >
      📊 Delivery History ({assignments.length})
    </button>
  </div>
</div>

      {/* Tab Content */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', minHeight: '400px' }}>
        
        {/* Current Delivery Tab - Updated with Address Details */}
{activeTab === 'current' && (
  <div>
    <h2 style={{ color: '#333', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
      {currentAssignment ? '📦 Current Delivery Assignment' : '📦 No Active Deliveries'}
    </h2>
    
    {currentAssignment ? (
      <div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px', 
          marginBottom: '20px',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px'
        }}>
          <div>
            <h3 style={{ color: '#2e7d32', marginBottom: '15px' }}>Order Details</h3>
            <p><strong>Order Number:</strong> #{currentAssignment.order_id}</p>
            <p><strong>Customer:</strong> {currentAssignment.customer_name}</p>
            <p><strong>Phone:</strong> {currentAssignment.customer_phone}</p>
            <p><strong>Payment:</strong> {currentAssignment.payment_method}</p>
            <p><strong>Amount:</strong> ₹{currentAssignment.total_amount}</p>
          </div>
          <div>
            <h3 style={{ color: '#2e7d32', marginBottom: '15px' }}>Delivery Information</h3>
            <p><strong>Status:</strong> 
              <span style={{
                backgroundColor: getStatusStyle(currentAssignment.status).bg,
                color: getStatusStyle(currentAssignment.status).text,
                border: `1px solid ${getStatusStyle(currentAssignment.status).border}`,
                padding: '6px 12px',
                borderRadius: '20px',
                marginLeft: '8px',
                fontWeight: 'bold',
                fontSize: '12px',
                textTransform: 'uppercase',
                display: 'inline-block'
              }}>
                {currentAssignment.status.replace('_', ' ')}
              </span>
            </p>
            <p><strong>Assigned Date:</strong> {new Date(currentAssignment.assigned_at).toLocaleDateString()}</p>
            <p><strong>Assigned Time:</strong> {new Date(currentAssignment.assigned_at).toLocaleTimeString()}</p>
          </div>
        </div>

        {/* NEW: Delivery Address Section */}
        <div style={{ 
          backgroundColor: '#e8f5e8', 
          padding: '20px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          borderLeft: '4px solid #2e7d32'
        }}>
          <h3 style={{ color: '#2e7d32', marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
            📍 Delivery Address
          </h3>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '15px', 
            borderRadius: '6px',
            border: '1px solid #c8e6c9'
          }}>
            <p style={{ 
              margin: 0, 
              lineHeight: '1.5',
              fontSize: '14px',
              whiteSpace: 'pre-wrap'
            }}>
              {currentAssignment.delivery_address || 'Address not available'}
            </p>
          </div>
          {currentAssignment.customer_phone && (
            <p style={{ margin: '10px 0 0 0', color: '#666', fontSize: '14px' }}>
              <strong>Contact:</strong> {currentAssignment.customer_phone}
            </p>
          )}
        </div>

        <div style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '6px' }}>
          <h3 style={{ margin: '0 0 15px 0' }}>Update Delivery Status</h3>
          <p style={{ margin: '0 0 15px 0', color: '#666', fontSize: '14px' }}>
            Please update the status as you progress with the delivery:
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {currentAssignment.status === 'assigned' && (
              <button
                onClick={() => updateDeliveryStatus(currentAssignment.id, 'picked_up')}
                style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                ✅ Mark as Picked Up
              </button>
            )}
            
            {currentAssignment.status === 'picked_up' && (
              <button
                onClick={() => updateDeliveryStatus(currentAssignment.id, 'in_transit')}
                style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                🚚 Mark as In Transit
              </button>
            )}
            
            {currentAssignment.status === 'in_transit' && (
              <button
                onClick={() => updateDeliveryStatus(currentAssignment.id, 'delivered')}
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                🎉 Mark as Delivered
              </button>
            )}
          </div>
        </div>
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <p style={{ fontSize: '18px', marginBottom: '10px' }}>No active delivery assignments</p>
        <p>You will see new delivery assignments here when they are assigned to you.</p>
        <p style={{ marginTop: '15px', fontStyle: 'italic' }}>Thank you for your patience!</p>
      </div>
    )}
  </div>
)}

        {/* Available Orders Tab - Updated with Address Column */}
{activeTab === 'available' && (
  <div>
    <h2 style={{ color: '#333', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
      📋 Available Orders for Assignment ({availableOrders.length})
    </h2>
    
    {availableOrders.length > 0 ? (
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Order #</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Customer</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Amount</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Payment</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Address</th> {/* NEW COLUMN */}
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Created</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {availableOrders.map((order) => {
              const statusStyle = getStatusStyle(order.status);
              return (
                <tr key={order.order_id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', fontWeight: 'bold' }}>#{order.order_id}</td>
                  <td style={{ padding: '12px' }}>
                    <div>
                      <strong>{order.customer_name}</strong>
                      <br />
                      <small style={{ color: '#666' }}>{order.customer_phone}</small>
                    </div>
                  </td>
                  <td style={{ padding: '12px', fontWeight: 'bold' }}>₹{order.total_amount}</td>
                  <td style={{ padding: '12px' }}>{order.payment_method}</td>
                  <td style={{ padding: '12px', maxWidth: '200px' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#555',
                      lineHeight: '1.3'
                    }}>
                      {order.delivery_address}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.text,
                      border: `1px solid ${statusStyle.border}`,
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      display: 'inline-block',
                      minWidth: '80px',
                      textAlign: 'center'
                    }}>
                      {order.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px' }}>{order.created_at}</td>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => acceptOrder(order.order_id)}
                      style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                    >
                      Accept Order
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <p style={{ fontSize: '18px', marginBottom: '10px' }}>No available orders</p>
        <p>All orders have been assigned. New orders will appear here when available.</p>
        <p style={{ marginTop: '15px', fontStyle: 'italic' }}>Check back soon!</p>
      </div>
    )}
  </div>
)}

        {/* Delivery History Tab */}
        {activeTab === 'history' && (
          <div>
            <h2 style={{ color: '#333', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
              📊 Your Delivery History ({assignments.length} deliveries)
            </h2>
            
            {assignments.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Order #</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((assignment) => (
                      <tr key={assignment.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>#{assignment.order_id}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            backgroundColor: 
                              assignment.status === 'delivered' ? '#c8e6c9' :
                              assignment.status === 'cancelled' ? '#ffcdd2' : '#bbdefb',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {assignment.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>{new Date(assignment.assigned_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>No delivery history yet. Your completed deliveries will appear here.</p>
                <p style={{ marginTop: '10px', fontStyle: 'italic' }}>Your first delivery will be exciting!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Message */}
      <div style={{ textAlign: 'center', marginTop: '30px', padding: '15px', color: '#666', borderTop: '1px solid #e0e0e0' }}>
        <p>Thank you for delivering happiness with RurDel! 🌟</p>
        <p style={{ fontSize: '12px', marginTop: '5px' }}>Need help? Contact support at support@rurdel.com</p>
      </div>
    </div>
  );
};

export default DeliveryAgentDashboard;